import {
  FC,
  SyntheticEvent,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Autocomplete, CircularProgress, FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import { ReactComponent as SearchIcon } from '../../assets/images/search-normal.svg';
import { defaultPagination } from '../../utils/constants/defaults';
import CustomersService from '../../services/customers';
import { customerAdapter } from '../../utils/adapters/userAdapter';
import { UserInterface } from '../../utils/types/user';
import { debounce } from '../../utils/lib';

export type SelectedOption = { label: string; value: string };

interface AutocompleteArgs {
  name: string;
  value: string | string[] | null;
  onChange: (event: SyntheticEvent, value: SelectedOption | SelectedOption[] | null) => void;
  errorSummary: boolean;
  multiple?: boolean;
}

const CUSTOMERS_PER_PAGE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 48;

const basePagination = {
  ...defaultPagination,
  attributes: ['id', 'name', 'lastName', 'email', 'phone', 'createdAt']
};

const toId = (id: string | number) => String(id);

const toSelectedOption = (customer: UserInterface): SelectedOption => ({
  label: `${customer.name} ${customer.lastName} <${customer.email}>`,
  value: toId(customer.id)
});

const uniqueById = (items: SelectedOption[]) => {
  const map = new Map<string, SelectedOption>();
  items.forEach((item) => map.set(item.value, item));
  return Array.from(map.values());
};

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

const CustomerInputAutoComplete: FC<AutocompleteArgs> = (props: AutocompleteArgs) => {
  const isMultiple = props.multiple === true;
  const customersServiceRef = useRef(new CustomersService());

  const [options, setOptions] = useState<SelectedOption[]>([]);
  const [selected, setSelected] = useState<SelectedOption | SelectedOption[] | null>(
    isMultiple ? [] : null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const valueIds = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(props.value) ? props.value.map(toId) : [];
    }
    return typeof props.value === 'string' && props.value ? [toId(props.value)] : [];
  }, [isMultiple, props.value]);

  const getSelectedIds = () => {
    if (!selected) return [];
    if (Array.isArray(selected)) return selected.map((item) => item.value);
    return [selected.value];
  };

  const autocompleteOptions = useMemo(
    () => uniqueById([...options, ...(Array.isArray(selected) ? selected : selected ? [selected] : [])]),
    [options, selected]
  );

  const fetchCustomersPage = useCallback(async (pageToLoad: number, search: string, append: boolean) => {
    const isLoadMore = append && pageToLoad > 1;
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    try {
      const searchQuery = search.trim()
        ? `name:${search.trim()},email:${search.trim()}`
        : undefined;

      const response = await customersServiceRef.current.findAll({
        ...basePagination,
        page: pageToLoad,
        perPage: CUSTOMERS_PER_PAGE,
        search: searchQuery
      });

      const mapped = response.results.map((customer) =>
        toSelectedOption(customerAdapter(customer))
      );

      setOptions((prev) => (append ? uniqueById([...prev, ...mapped]) : mapped));

      const total = response.pagination?.total ?? 0;
      const loadedCount = (pageToLoad - 1) * CUSTOMERS_PER_PAGE + mapped.length;
      setHasMore(loadedCount < total);
      setPage(pageToLoad);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        setPage(1);
        setHasMore(true);
        fetchCustomersPage(1, term, false);
      }, 500),
    [fetchCustomersPage]
  );

  useEffect(() => {
    fetchCustomersPage(1, '', false);
  }, [fetchCustomersPage]);

  useEffect(() => {
    if (sameIds(valueIds, getSelectedIds())) return;

    if (!valueIds.length) {
      setSelected(isMultiple ? [] : null);
      return;
    }

    let cancelled = false;

    const syncFromProps = async () => {
      const optionMap = new Map(options.map((option) => [option.value, option]));
      const resolved: SelectedOption[] = [];

      valueIds.forEach((id) => {
        const match = optionMap.get(id);
        if (match) resolved.push(match);
      });

      const missingIds = valueIds.filter((id) => !resolved.some((item) => item.value === id));

      if (missingIds.length) {
        const fetched = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const customer = await customersServiceRef.current.findOne(id);
              return toSelectedOption(customerAdapter(customer));
            } catch {
              return null;
            }
          })
        );
        fetched.filter(Boolean).forEach((item) => resolved.push(item as SelectedOption));
      }

      if (cancelled) return;

      const ordered = valueIds
        .map((id) => resolved.find((item) => item.value === id))
        .filter((item): item is SelectedOption => Boolean(item));

      setSelected(isMultiple ? ordered : ordered[0] ?? null);

      if (ordered.length) {
        setOptions((prev) => uniqueById([...prev, ...ordered]));
      }
    };

    syncFromProps();

    return () => {
      cancelled = true;
    };
  }, [isMultiple, options, valueIds]);

  const loadNextPage = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchCustomersPage(page + 1, searchTerm, true);
  }, [fetchCustomersPage, hasMore, loading, loadingMore, page, searchTerm]);

  const handleListboxScroll = (event: UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    const nearBottom =
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;

    if (nearBottom) loadNextPage();
  };

  const handleInputChange = (_event: SyntheticEvent, inputValue: string, reason: string) => {
    if (reason === 'reset') return;
    setSearchTerm(inputValue);
    debouncedSearch(inputValue);
  };

  const handleAutocompleteChange = (
    _event: SyntheticEvent,
    value: SelectedOption | SelectedOption[] | null
  ) => {
    const nextValue = isMultiple
      ? (Array.isArray(value) ? value : [])
      : Array.isArray(value)
        ? null
        : value;

    setSelected(nextValue);
    props.onChange(_event, nextValue);
  };

  return (
    <FormControl style={{ width: '100%' }} error={props.errorSummary}>
      <FormLabel component="label" className={'regular'}>
        {isMultiple ? 'Owners' : 'Owner'}
      </FormLabel>
      <Autocomplete
        multiple={isMultiple}
        disablePortal
        filterOptions={(x) => x}
        options={autocompleteOptions}
        getOptionLabel={(option) => option.label}
        loading={loading || loadingMore}
        value={selected}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={handleAutocompleteChange}
        onInputChange={handleInputChange}
        popupIcon={<SearchIcon />}
        ListboxProps={{
          onScroll: handleListboxScroll,
          style: { maxHeight: 280 }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={props.errorSummary}
            type="text"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || loadingMore) && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
      {props.errorSummary && (
        <FormHelperText id="my-helper-text">
          {isMultiple ? 'At least one owner is required' : 'Owner is not allowed to be empty'}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default CustomerInputAutoComplete;
